import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';
import PB101OutcomeModal from './PB101OutcomeModal';
import ContactLines from '../../components/ContactLines';

// ─── Admin-only PB101 kanban pipeline board ──────────────────────────────────
// Per docs/COMS_PB101_Pipeline_Redesign_Handoff_2026-09-17.md — a new,
// admin-only view (gated at the route level, see App.jsx) grouped by who
// owns the work rather than funnel order. Does not touch PB101Tasks.jsx or
// the existing Daily Tasks flow FD/Sales use today; reads a dedicated
// GET /pb101/kanban endpoint (getPB101KanbanBoard in queries.js) and reuses
// the existing PB101OutcomeModal/POST /pb101/outcome contract for logging.
// Card movement between columns is driven by backend classification
// (classifyKanbanStage) + outcome logging, matching the approved mockup
// (https://claude.ai/artifact/PDKJUSJPeCyjSgUae5PtE8). Drag-and-drop (BK,
// 9/18/26) is layered on top as a SHORTCUT to those same actions, never a
// raw state override — see DROP_TARGETS below and handleCardDrop.

const STAGE_META = {
  registered:          { num: 1, label: 'Registered + OP Invite',   group: 'club',    desc: 'Open Play invite sent, no reply yet' },
  // Split out from "Registered" (BK, 9/18/26) — a distinct milestone
  // (confirmed booked into Open Play), and having it as its own column
  // gives a real headcount: how many are locked in for the upcoming Open
  // Play session vs. how many No-Show/Re-Engage contacts are worth working
  // to help fill it out. Not a real backend stage — see registeredBooked/
  // registeredWaiting split below; cards here are still classified
  // 'registered' for script/outcome/drag purposes, this is a display-only
  // partition on the same openPlayReserved flag already tracked.
  bookedOpenPlay:       { num: 2, label: 'Booked Open Play',        group: 'club',    desc: 'Confirmed — reminder script only' },
  // tip: training-note guidance shown in the column header (BK, 9/18/26) —
  // reinforces the "trial first, then a fallback ask" strategy right where
  // staff are working, not just in the one-time training video.
  dayOfTrialAsk:       { num: 3, label: 'PB101 Session · Trial Ask', group: 'club',   desc: 'Arrived → stayed for OP → trial offered',
    tip: "Trial's a no today? Offer Open Play, or ask about their group for a comp'd reservation." },
  // 10/23/26 (BK): the club owns getting someone booked after PB101, ideally into a trial. This is the old Post-Session Follow-Up and Free
  // Play Re-Engage in one list (up to 120 days). Two logged attempts on different days, still not booked, and the card moves to Inside
  // Sales' "Not booked after PB101".
  nextStep:            { num: 4, label: 'Book their next step',     group: 'club',    desc: 'Offer a trial or book a session · 2 attempts, then Inside Sales',
    tip: "Trial first. If it's a no, book Open Play, check beginner programming for the next 2 weeks, or offer a comp'd group reservation." },
  noShow:              { num: 5, label: 'No-Show Re-Invite',        group: 'club',    desc: 'Never attended · up to 3 tries, weekly' },
  followUpClub:        { num: 6, label: 'Follow Up Scheduled',      group: 'club',    desc: 'Club follow-up on the calendar · red once overdue' },
  readyForSales:       { num: 7, label: 'Ready for Sales',          group: 'sales',   desc: 'Attended 2+ times · ready for the membership ask' },
  followUpScheduled:   { num: 8, label: 'Follow Up Scheduled',      group: 'sales',   desc: 'Inside Sales follow-up on the calendar · red once overdue' },
  notBooked:           { num: 9, label: 'Not booked after PB101',   group: 'sales',   desc: 'Club tried twice · Inside Sales works the booking gap' },
  notReadyYet:         { num: 10, label: 'Not Ready Yet',           group: 'sales',   desc: 'FD moved here — sales nurtures from here on' },
  closed:              { num: 11, label: 'Closed',                  group: 'outcome', desc: 'Converted to member, or started a trial' },
  declined:            { num: 12, label: 'Declined',                group: 'outcome', desc: 'Said no — an active refusal' },
  lost:                { num: 13, label: 'Lost',                    group: 'outcome', desc: 'Went quiet — 120 days, no booking' },
};

const GROUPS = [
  { key: 'club',    label: 'Club · Front Desk', color: '#eab308', stages: ['registered', 'bookedOpenPlay', 'dayOfTrialAsk', 'nextStep', 'noShow', 'followUpClub'] },
  { key: 'sales',   label: 'Inside Sales',      color: '#3b82f6', stages: ['readyForSales', 'followUpScheduled', 'notBooked', 'notReadyYet'] },
  { key: 'outcome', label: 'Outcomes',          color: '#888',    stages: ['closed', 'declined', 'lost'] },
];

// Every phase in PRESESSION_INVITE_PHASES (PB101OutcomeModal.jsx) routes to
// OPEN_PLAY_INVITE_RESULTS; anything else routes to TRIAL_ASK_RESULTS (which
// now includes 'Not Ready Yet'). We only need to land on the right side of
// that split, not reproduce getTaskPhase's exact value.
const PRESESSION_TASK_PHASE = 'presend';
const POSTSESSION_TASK_PHASE = 'followup';

// Milestone list (BK, 9/18/26) — "routed to next level / booked into more
// programming" is a general fallback goal for anyone who got the ask and
// didn't buy, not just an explicit "Not Ready Yet". Available on all three
// stages where a trial ask has happened or is in progress.
const BOOK_PROGRAM_STAGES = ['dayOfTrialAsk', 'nextStep', 'followUpClub', 'readyForSales', 'followUpScheduled', 'notBooked', 'notReadyYet', 'noShow'];

// Drag-and-drop target map (BK, 9/18/26: "drag = shortcut for an existing
// action"). Dropping a card onto one of these columns opens the SAME outcome
// modal "Log outcome" already triggers, pre-selecting the outcome where
// there's exactly one unambiguous match; Closed/Declined have several
// possible reasons/plans so those open unpicked, same as clicking the button
// normally would. Any drop not listed here is a no-op — nothing moves
// without a real outcome getting logged and saved.
// Keyed by DISPLAY column (metaKey), not the underlying real stage — matters
// for bookedOpenPlay specifically, which shares stage='registered' with the
// plain Registered column but is a distinct drop target (BK, 9/18/26).
// direct: 'mark_no_show' bypasses the outcome modal entirely — this is a
// plain attendance fact (attended_ltp=false), not a logged contact outcome,
// same pattern the Attended toggle's own "No" button already uses. Every
// other target opens the outcome modal (handleLogOutcome), matching "drag
// is a shortcut, never a silent override" (BK, 9/18/26 confirmed this
// principle, then asked to widen coverage to "all the stages" that have a
// real backing action).
const DROP_TARGETS = {
  bookedOpenPlay: { presetOutcome: 'Booked FREE Open Play', from: ['registered'] },
  // Not valid pre-session (registered/bookedOpenPlay) — the session hasn't
  // happened yet, so "no show" has no meaning there.
  noShow:      { direct: 'mark_no_show', from: ['dayOfTrialAsk', 'nextStep', 'followUpClub', 'readyForSales', 'followUpScheduled', 'notBooked'] },
  notReadyYet: { presetOutcome: 'Not Ready Yet', from: ['dayOfTrialAsk', 'nextStep', 'followUpClub', 'followUpScheduled', 'notBooked', 'noShow'] },
  lost:        { presetOutcome: 'Lost — No Response', from: ['dayOfTrialAsk', 'nextStep', 'followUpClub', 'readyForSales', 'followUpScheduled', 'notBooked', 'notReadyYet', 'noShow'] },
  closed:      { presetOutcome: null, from: ['dayOfTrialAsk', 'nextStep', 'followUpClub', 'readyForSales', 'followUpScheduled', 'notBooked', 'notReadyYet', 'noShow'] },
  // 'Declined FREE Open Play' is a real option even pre-session (someone
  // who booked OP can still say no before their session), so unlike lost/
  // closed this legitimately includes 'registered'.
  declined:    { presetOutcome: null, from: ['registered', 'dayOfTrialAsk', 'nextStep', 'followUpClub', 'readyForSales', 'followUpScheduled', 'notBooked', 'notReadyYet', 'noShow'] },
};

function scriptCueForCard(stage, record) {
  const d = record.sessionDateRaw
    ? Math.round((new Date(record.sessionDateRaw + 'T12:00:00') - new Date()) / 86400000)
    : null;

  if (stage === 'registered') {
    if (record.openPlayReserved) return { key: 'pb101_op_morning_of', note: 'OP reserved ✅ — reminder' };
    if (d !== null && d <= 1) return { key: 'pb101_op_followup_24hr', note: 'Day before — confirm attendance' };
    if (record.openPlayInvited) return { key: 'pb101_op_invite_registration', note: 'Invited, not reserved — nudge' };
    if (d !== null && d >= 5) return { key: 'pb101_presession_5day', note: 'Send once · 5+ days out' };
    return { key: 'pb101_op_invite_registration', note: `${d ?? '?'}D out — send Open Play invite` };
  }
  if (stage === 'dayOfTrialAsk') {
    return record.stayedForPlay
      ? { key: 'pb101_trial_asked_stayed', note: 'Stayed for Open Play — strike now' }
      : { key: 'pb101_trial_ask_notstayed', note: "Didn't stay for Open Play — softer ask" };
  }
  if (stage === 'nextStep' || stage === 'followUpClub') {
    // By timing (BK, 10/23/26): a fresh session gets the two follow-up scripts (first attempt, then last chance); after three weeks the
    // Open Play hook is the better opener.
    if ((record.daysSinceSession ?? 0) > 21) return { key: 'pb101_op_reengage', note: 'Free Open Play hook' };
    return (record.attemptCount || 0) === 0
      ? { key: 'pb101_followup_1', note: '1st attempt' }
      : { key: 'pb101_followup_2', note: 'Last chance' };
  }
  if (stage === 'followUpScheduled' || stage === 'readyForSales' || stage === 'notBooked') {
    return (record.followupCount || 0) === 0
      ? { key: 'pb101_followup_1', note: '1st attempt' }
      : { key: 'pb101_followup_2', note: 'Last chance' };
  }
  if (stage === 'noShow') {
    const daysPast = record.sessionDateRaw
      ? Math.round((new Date() - new Date(record.sessionDateRaw + 'T12:00:00')) / 86400000)
      : null;
    return daysPast !== null && daysPast > 42
      ? { key: 'pb101_no_show_reinvite_stale', note: 'Reschedule ask — a while back' }
      : { key: 'pb101_no_show_reinvite_recent', note: 'Reschedule ask' };
  }
  return null;
}

// PB101 sessions are fixed Wed 6PM / Sat 10AM — same convention used
// throughout queries.js (hoursUntilSession) and PB101OutcomeModal.jsx.
// isOpenPlayTime (BK, 9/18/26): Free Open Play runs AFTER the PB101 class,
// not at the same time — only pb101_op_reengage's [Day]/[Time] refers to
// Open Play itself; every other script's [Day]/[Time] means the class.
function dayTimeFromSession(sessionDateRaw, isOpenPlayTime) {
  if (!sessionDateRaw) return { day: null, time: null };
  const dow = new Date(sessionDateRaw + 'T12:00:00').getDay();
  if (dow === 3) return { day: 'Wednesday', time: isOpenPlayTime ? '7:00 PM' : '6:00 PM' };
  if (dow === 6) return { day: 'Saturday', time: isOpenPlayTime ? '11:00 AM' : '10:00 AM' };
  return { day: null, time: null };
}

function fillScript(text, firstName, clubName, sessionDateRaw, isOpenPlayTime) {
  if (!text) return text;
  const displayClub = clubName || 'Picklr';
  const { day, time } = dayTimeFromSession(sessionDateRaw, isOpenPlayTime);
  const dayTimeFilled = day
    ? text.replace(/\[Day\]/g, day).replace(/\[Time\]/g, time)
    : text.replace(/\[Day\] at \[Time\]/g, 'your upcoming session')
          .replace(/\[Day\]/g, 'the upcoming session')
          .replace(/\[Time\]/g, '');
  return dayTimeFilled
    .replace(/\[Name\]/g, firstName)
    .replace(/\[Rep Name\] from The Picklr \[Club Name\]/g, displayClub)
    .replace(/\[Rep Name\]/g, displayClub)
    .replace(/The Picklr \[Club Name\]/g, displayClub)
    .replace(/\[Club Name\]/g, displayClub);
}

// Compact by design (BK, 9/17/26 — fit the whole board on one screen): no
// inline script paragraph. The full text still gets copied in full; the card
// just doesn't spend vertical/horizontal space rendering it — tap "Switch
// script"/expand isn't needed here since PB101OutcomeModal's ScriptPanel
// already shows the full text when a rep actually logs the outcome.
function ScriptLine({ cue, scripts, record, clubName }) {
  const [copied, setCopied] = useState(false);
  if (!cue) return null;
  const script = scripts?.find(s => s.script_key === cue.key) || null;
  const firstName = record.name?.split(' ')[0] || '[Name]';
  const opener = `Hey ${firstName}, this is ${clubName || 'Picklr'}.`;
  const body = fillScript(script?.body, firstName, clubName, record.sessionDateRaw, cue.key === 'pb101_op_reengage');
  const full = body ? `${opener}\n\n${body}` : opener;

  return (
    <button
      className="btn btn-ghost btn-sm"
      style={{ width: '100%', fontSize: 10, padding: '4px 6px', marginTop: 4 }}
      title={body || 'Script not yet seeded'}
      onClick={e => {
        e.stopPropagation();
        navigator.clipboard.writeText(full).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }}
    >
      {copied ? '✓ Copied' : `📋 ${cue.note}`}
    </button>
  );
}

function Badge({ children, color }) {
  return (
    <span style={{
      background: `${color}22`, color, border: `1px solid ${color}`,
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4, display: 'inline-block', marginBottom: 4,
    }}>
      {children}
    </span>
  );
}

function pillStyle(active, color) {
  return {
    fontSize: 9.5, fontWeight: 600, padding: '2px 8px', borderRadius: 20, cursor: 'pointer',
    border: `1px solid ${active ? color : '#333'}`,
    background: active ? `${color}22` : 'transparent',
    color: active ? color : '#888',
  };
}

// Milestone checkpoint row (BK, 9/18/26 milestone list) — Arrived / Stayed
// for OP / Trial Offered, the three sub-steps inside the day-of-event stage.
// Purely a read of already-tracked fields (attended_ltp, stayed_for_play,
// trial_ask_result via the trialOffered flag the backend now derives) —
// gives a glance-able status without opening the card's controls.
function CheckpointRow({ record }) {
  const attendedSet = record.attendedLtp !== null && record.attendedLtp !== undefined;
  const attended = attendedSet && (record.attendedLtp === true || record.attendedLtp === 'true');
  const stayedSet = record.stayedForPlay !== null && record.stayedForPlay !== undefined;
  const stayed = stayedSet && (record.stayedForPlay === true || record.stayedForPlay === 'true');

  function dot(label, state) {
    const color = state === 'yes' ? '#22c55e' : state === 'no' ? '#ef4444' : '#555';
    const mark = state === 'yes' ? '✓' : state === 'no' ? '✕' : state === 'na' ? '–' : '○';
    return <span style={{ color, marginRight: 7 }}>{mark} {label}</span>;
  }

  return (
    <div style={{ fontSize: 9, marginBottom: 4, lineHeight: 1.6 }}>
      {dot('Arrived', !attendedSet ? 'pending' : attended ? 'yes' : 'no')}
      {dot('Stayed', (!attendedSet || !attended) ? 'na' : !stayedSet ? 'pending' : stayed ? 'yes' : 'no')}
      {dot('Offered', record.trialOffered ? 'yes' : 'pending')}
    </div>
  );
}

// Attendance capture (BK, 9/17/26) — Day-of Trial Ask's "Log attendance + ask"
// button previously only opened the generic outcome modal and never actually
// wrote attended_ltp/stayed_for_play; those fields only got set via the
// separate Record Detail page. Writes directly via the existing record-update
// endpoint (both fields are already in updatePB101Record's allowed list) —
// no new column, and stayed_for_play also drives which trial-ask script
// scriptCueForCard shows, so setting it here fixes script accuracy too.
function AttendanceToggle({ record, onRefresh, put }) {
  const [busy, setBusy] = useState(false);
  const attendedSet = record.attendedLtp !== null && record.attendedLtp !== undefined;
  const attended = attendedSet && (record.attendedLtp === true || record.attendedLtp === 'true');
  const stayedSet = record.stayedForPlay !== null && record.stayedForPlay !== undefined;
  const stayed = stayedSet && (record.stayedForPlay === true || record.stayedForPlay === 'true');

  async function save(payload) {
    setBusy(true);
    try {
      await put(`/pb101/record/${record.id}`, payload);
      onRefresh();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ margin: '4px 0' }}>
      <div style={{ fontSize: 9, color: '#888', marginBottom: 2 }}>Attended?</div>
      <div style={{ display: 'flex', gap: 4 }}>
        <button disabled={busy} style={pillStyle(attendedSet && attended, '#22c55e')} onClick={e => { e.stopPropagation(); save({ attended_ltp: true }); }}>Yes</button>
        <button disabled={busy} style={pillStyle(attendedSet && !attended, '#ef4444')} onClick={e => { e.stopPropagation(); save({ attended_ltp: false, stayed_for_play: false }); }}>No</button>
      </div>
      {attendedSet && attended && (
        <>
          <div style={{ fontSize: 9, color: '#888', margin: '4px 0 2px' }}>Stayed for Open Play?</div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button disabled={busy} style={pillStyle(stayedSet && stayed, '#22c55e')} onClick={e => { e.stopPropagation(); save({ stayed_for_play: true }); }}>Yes</button>
            <button disabled={busy} style={pillStyle(stayedSet && !stayed, '#ef4444')} onClick={e => { e.stopPropagation(); save({ stayed_for_play: false }); }}>No</button>
          </div>
        </>
      )}
    </div>
  );
}

// Surfaces outcome_log.follow_up_date directly on the card (BK, 9/17/26) —
// previously only visible on the separate Follow-Ups page, so a scheduled
// "Contacted — Waiting Response" follow-up was invisible on this board even
// though the record just sat in its stage unchanged. Same red/orange/blue
// urgency convention as the existing /lead/followups endpoint.
function FollowUpBadge({ record }) {
  if (!record.pendingFollowUpDate) return null;
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const color = record.pendingFollowUpDate <= today ? '#ef4444'
    : record.pendingFollowUpDate === tomorrow ? '#f59e0b'
    : '#3b82f6';
  const label = new Date(record.pendingFollowUpDate + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  return <Badge color={color}>📅 Follow up {label}</Badge>;
}

// Bug found 9/22/26 (McKinney pilot, BK): logging an outcome like "Contacted
// — Waiting Response" left zero visible trace on the card, so a second (then
// third) rep had no way to know someone was already reached out to. The data
// (last_contact_date) was already computed server-side, just never mapped
// through to the frontend (see toPB101Card, queries.js) or rendered anywhere.
// Shown on every non-terminal stage, not just the ones that already had an
// attempt counter — this was invisible on stage 1 (Registered) specifically,
// which is the stage this bug was reported on.
function LastContactBadge({ record }) {
  if (!record.lastContactDate) return null;
  const label = new Date(record.lastContactDate).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  return <Badge color="#a78bfa">✓ Contacted {label}</Badge>;
}

function KanbanCard({ record, stage, metaKey, scripts, clubName, onLogOutcome, onBookFreeProgram, onRefresh, put }) {
  const [busy, setBusy] = useState(false);
  const cue = scriptCueForCard(stage, record);
  const isTerminal = stage === 'closed' || stage === 'declined' || stage === 'lost';

  async function handleBookFreeProgram() {
    setBusy(true);
    try {
      await put(`/pb101/record/${record.id}`, { open_play_reserved: true });
      onRefresh();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      draggable={!isTerminal}
      onDragStart={!isTerminal ? (e) => {
        e.dataTransfer.setData('application/json', JSON.stringify({ id: record.id, stage }));
        e.dataTransfer.effectAllowed = 'move';
      } : undefined}
      onClick={!isTerminal ? () => onLogOutcome(record, stage) : undefined}
      style={{
        background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7,
        padding: '7px 8px', marginBottom: 6,
        cursor: isTerminal ? 'default' : 'pointer',
      }}
    >
      <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{record.name}</div>
      <ContactLines phone={record.phone} email={record.email} name={record.name} clubId={record.clubId} size="xs" />
      <div style={{ fontSize: 9.5, color: '#888', fontFamily: 'monospace', marginBottom: 4, lineHeight: 1.3 }}>
        {record.sessionDate || '—'}
        {isTerminal && record.updatedAt ? ` · ${new Date(record.updatedAt).toLocaleDateString()}` : ''}
        {stage === 'nextStep' || stage === 'followUpClub' ? ` · att. ${Math.min((record.attemptCount || 0) + 1, 2)}/2` : ''}
        {stage === 'noShow' ? ` · att. ${(record.reinviteCount || 0) + 1}/3` : ''}
      </div>

      {!isTerminal && (record.pendingFollowUpDate || record.lastContactDate) && (
        <div style={{ marginBottom: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <LastContactBadge record={record} />
          <FollowUpBadge record={record} />
        </div>
      )}

      {stage === 'lost' && record.lostReason && (
        <div style={{ fontSize: 9, color: '#888', marginBottom: 4, lineHeight: 1.4 }}>{record.lostReason}</div>
      )}

      {(record.openPlayReserved || (stage === 'registered' && record.openPlayInvited) || stage === 'dayOfTrialAsk'
        || ((stage === 'nextStep' || stage === 'followUpClub') && record.daysSinceSession != null)
        || (stage === 'notBooked' && (record.attempts || []).length > 0)
        || (stage === 'noShow' && (record.reinviteCount || 0) >= 2)
        || (isTerminal && stage === 'closed') || record.priorVisits > 0 || record.isResurfacing || (record.attendedCount || 0) >= 2) && (
        <div>
          {metaKey === 'bookedOpenPlay' ? null : stage === 'registered' ? (
            record.openPlayReserved
              ? <Badge color="#22c55e">✓ Accepted</Badge>
              : record.openPlayInvited && <Badge color="#3b82f6">📨 Invited</Badge>
          ) : (
            record.openPlayReserved && <Badge color="#22c55e">✓ Booked</Badge>
          )}
          {stage === 'dayOfTrialAsk' && <Badge color="#ef4444">Today</Badge>}
          {(stage === 'nextStep' || stage === 'followUpClub') && record.daysSinceSession != null && (
            <Badge color={record.daysSinceSession <= 3 ? '#22c55e' : record.daysSinceSession <= 7 ? '#f59e0b' : '#ef4444'}>
              {record.daysSinceSession === 0 ? 'Today' : `${record.daysSinceSession}d since session`}
            </Badge>
          )}
          {(stage === 'nextStep' || stage === 'followUpClub') && (record.attemptCount || 0) >= 1 && <Badge color="#ef4444">Last try</Badge>}
          {stage === 'noShow' && (record.reinviteCount || 0) >= 2 && <Badge color="#ef4444">Final try</Badge>}
          {isTerminal && stage === 'closed' && (record.onTrial
            ? <Badge color="#22c55e">On a trial</Badge>
            : <Badge color="#22c55e">Won — {record.recurringConverted ? 'mbr' : (record.saleType || 'trial')}</Badge>)}
          {(record.attendedCount || 0) >= 2 && !isTerminal && <Badge color="#22c55e">★ Ready · {record.attendedCount} sessions</Badge>}
          {record.priorVisits > 0 && (record.attendedCount || 0) < 2 && <Badge color="#a855f7">🔁 Visit #{record.priorVisits + 1}</Badge>}
          {record.isResurfacing && <Badge color="#eab308">↩ Resurfacing — quiet 30d+</Badge>}
        </div>
      )}

      {stage === 'notBooked' && (record.attempts || []).length > 0 && (
        <div style={{ fontSize: 9.5, color: '#a8a496', margin: '2px 0 4px', lineHeight: 1.45 }}>
          <div style={{ color: '#888' }}>Club tried {(record.attempts || []).length}×, no booking:</div>
          {(record.attempts || []).slice(-3).map((a, i) => (
            <div key={i}>
              {new Date(a.date + 'T12:00:00').toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
              {a.method ? ` · ${a.method}` : ''}{a.by ? ` · ${a.by}` : ''}{a.outcome ? ` · ${a.outcome === 'Contacted — Waiting Response' ? 'waiting response' : a.outcome.toLowerCase()}` : ''}
            </div>
          ))}
        </div>
      )}
      {stage === 'dayOfTrialAsk' && <CheckpointRow record={record} />}
      {stage === 'dayOfTrialAsk' && <AttendanceToggle record={record} onRefresh={onRefresh} put={put} />}

      {!isTerminal && cue && <ScriptLine cue={cue} scripts={scripts} record={record} clubName={clubName} />}
      {stage === 'notReadyYet' && !cue && (
        <div style={{ fontSize: 9.5, color: '#a8a496', margin: '4px 0', lineHeight: 1.35 }}>
          {record.openPlayReserved
            ? 'Free program booked — goal met.'
            : 'No read on fit yet — your call.'}
        </div>
      )}

      {!isTerminal && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          {BOOK_PROGRAM_STAGES.includes(stage) && !record.openPlayReserved && (
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={e => { e.stopPropagation(); handleBookFreeProgram(); }} style={{ fontSize: 10, padding: '4px 6px' }}>
              {busy ? 'Saving…' : 'Book free program'}
            </button>
          )}
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: '4px 6px' }} onClick={e => { e.stopPropagation(); onLogOutcome(record, stage); }}>
            {stage === 'dayOfTrialAsk' ? 'Log attendance + ask' : 'Log outcome'}
          </button>
        </div>
      )}
    </div>
  );
}

function Column({ stage, metaKey, records, scripts, clubName, onLogOutcome, onRefresh, put, onCardDrop }) {
  const displayKey = metaKey || stage;
  const meta = STAGE_META[displayKey];
  const [dragOver, setDragOver] = useState(false);
  const isDropTarget = !!DROP_TARGETS[displayKey];
  // "Invited" count only means something on the not-yet-booked Registered
  // column now — Booked Open Play's own count badge IS the "accepted"
  // number (BK, 9/18/26: wanted this split specifically to compare it
  // against No-Show/Re-Engage headcounts as a fill-the-session gauge).
  const invitedCount  = displayKey === 'registered' ? records.filter(r => r.openPlayInvited).length : null;
  const attendedCount = stage === 'dayOfTrialAsk' ? records.filter(r => r.attendedLtp === true || r.attendedLtp === 'true').length : null;
  return (
    <div
      style={{
        flex: '0 0 158px', width: 158,
        outline: dragOver && isDropTarget ? '2px solid #eab308' : 'none',
        borderRadius: 8,
      }}
      onDragOver={isDropTarget ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
      onDragLeave={isDropTarget ? () => setDragOver(false) : undefined}
      onDrop={isDropTarget ? (e) => {
        e.preventDefault();
        setDragOver(false);
        try {
          const dragged = JSON.parse(e.dataTransfer.getData('application/json'));
          onCardDrop(dragged.id, dragged.stage, displayKey);
        } catch (err) { /* ignore malformed drag payload */ }
      } : undefined}
    >
      <div style={{ background: '#1c1c1c', borderRadius: 7, padding: '5px 7px', marginBottom: 6 }}>
        <span style={{
          float: 'right', fontFamily: 'monospace', fontSize: 10, fontWeight: 500,
          background: '#0a0a0a', color: '#a8a496', padding: '1px 6px', borderRadius: 20,
        }}>{records.length}</span>
        <div style={{ fontWeight: 700, fontSize: 10, color: '#a8a496', lineHeight: 1.3 }}>{meta.num} · {meta.label}</div>
        <div style={{ fontSize: 9, color: '#666', marginTop: 1, lineHeight: 1.3 }}>{meta.desc}</div>
        {meta.tip && (
          <div style={{
            fontSize: 8.5, color: '#f6cf4a', marginTop: 4, lineHeight: 1.4,
            background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)',
            borderRadius: 5, padding: '3px 5px',
          }}>
            💡 {meta.tip}
          </div>
        )}
        {displayKey === 'registered' && (
          <div style={{ fontSize: 9, color: '#3b82f6', marginTop: 3, lineHeight: 1.3 }}>
            📨 {invitedCount} invited, awaiting response
          </div>
        )}
        {stage === 'dayOfTrialAsk' && records.length > 0 && (
          // Green only once fully resolved (BK, 9/19/26 — this was hardcoded
          // green regardless of the ratio, so "1 of 5 attended" would have
          // shown the same "all good" color as "5 of 5"). Gray mid-day is
          // just "still checking people in," not a problem worth flagging —
          // a genuine same-day no-show already leaves this column entirely
          // (classifyKanbanStage routes attended_ltp===false straight to
          // No-Show), so this count only ever splits attended vs. pending.
          <div style={{ fontSize: 9, color: attendedCount === records.length ? '#22c55e' : '#888', marginTop: 3, lineHeight: 1.3 }}>
            ✓ {attendedCount} of {records.length} attended
          </div>
        )}
      </div>
      {records.length === 0 && (
        <div style={{ fontSize: 10, color: '#555', textAlign: 'center', padding: '12px 4px', border: '1px dashed #2a2a2a', borderRadius: 7 }}>
          Nothing here
        </div>
      )}
      {records.map(r => (
        <KanbanCard
          key={r.id}
          record={r}
          stage={stage}
          metaKey={displayKey}
          scripts={scripts}
          clubName={clubName}
          onLogOutcome={onLogOutcome}
          onRefresh={onRefresh}
          put={put}
        />
      ))}
    </div>
  );
}

export default function PB101Kanban() {
  const { get, put } = useApi();
  const { currentClub } = useClub();
  const [board, setBoard] = useState(null);
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [outcomeTarget, setOutcomeTarget] = useState(null); // { record, taskPhase }

  // Previously a silent .catch(console.error) — a real 403 (pilot club/role
  // check) or 401 (auth) looked identical to "no data" in the UI, with the
  // actual reason invisible unless someone opened DevTools (BK, 9/19/26 —
  // "showing no pipeline" turned out to need real debugging to diagnose).
  const load = useCallback(() => {
    if (!currentClub?.id) { setLoading(false); return; }
    setLoading(true);
    setLoadError('');
    get(`/pb101/kanban?club=${currentClub.id}`)
      .then(setBoard)
      .catch(e => setLoadError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    get('/scripts?module=pb101').then(res => setScripts(res?.scripts || [])).catch(() => setScripts([]));
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const [invalidDropMsg, setInvalidDropMsg] = useState('');

  function handleLogOutcome(record, stage, presetOutcome) {
    // noShow/winBack pass their real stage through as taskPhase (matching
    // getTaskPhase's own vocabulary in queries.js) so PB101OutcomeModal
    // routes them to REENGAGE_RESULTS instead of the full trial-ask list
    // (BK, 9/19/26 — a reschedule call isn't a live trial pitch, and
    // "Failed to Ask"/"No Show" as outcome OPTIONS made no sense on a card
    // that already IS a no-show).
    // A next-step card older than three weeks is a re-engage conversation (Open Play hook), not a fresh post-session ask.
    const taskPhase = stage === 'registered' ? PRESESSION_TASK_PHASE
      : stage === 'noShow' ? stage
      : ((stage === 'nextStep' || stage === 'followUpClub') && (record.daysSinceSession ?? 0) > 21) ? 'winBack'
      : POSTSESSION_TASK_PHASE;
    setOutcomeTarget({ ...record, taskPhase, presetOutcome: presetOutcome || undefined });
  }

  async function handleCardDrop(recordId, sourceStage, targetStage) {
    if (sourceStage === targetStage) return;
    const target = DROP_TARGETS[targetStage];
    if (!target || !target.from.includes(sourceStage)) {
      setInvalidDropMsg('No direct action for that move — this stage is driven by attendance/date, not manually set.');
      setTimeout(() => setInvalidDropMsg(''), 3000);
      return;
    }
    if (target.direct === 'mark_no_show') {
      try {
        await put(`/pb101/record/${recordId}`, { attended_ltp: false, stayed_for_play: false });
        load();
      } catch (e) { console.error(e); }
      return;
    }
    const record = (board.stages?.[sourceStage] || []).find(r => r.id === recordId);
    if (!record) return;
    handleLogOutcome(record, sourceStage, target.presetOutcome);
  }

  if (loading) return <div className="empty-state"><div className="spinner" /></div>;
  if (loadError) return (
    <div className="empty-state">
      <p>Couldn't load the pipeline: {loadError}</p>
      <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
        Club: {currentClub?.id || 'none selected'}. If this says 403, this club/role isn't in the pilot list yet.
      </p>
    </div>
  );
  if (!board) return <div className="empty-state"><p>No pipeline data.</p></div>;

  const total = Object.values(board.counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="module-page kanban-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>🏓</span>
          <h1 className="module-title">PB101 Pipeline</h1>
        </div>
        <div style={{ fontSize: 12.5, color: '#888' }}>
          {currentClub?.name || ''} · {total} active records
        </div>
      </div>

      {invalidDropMsg && (
        <div style={{ fontSize: 11.5, color: '#ef4444', background: '#2d0a0a', border: '1px solid #ef444455', borderRadius: 6, padding: '5px 10px', marginBottom: 8 }}>
          {invalidDropMsg}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
        {GROUPS.map(group => (
          <div key={group.key} style={{ flex: 'none' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 2px 5px',
              marginBottom: 6, borderBottom: `2px solid ${group.color}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: group.color }}>
                {group.label}
              </span>
              {group.key === 'club' && (board.stages?.notBooked || []).length > 0 && (
                <span style={{ fontSize: 10, color: '#3b82f6', marginLeft: 6 }}>→ {(board.stages.notBooked || []).length} handed to Inside Sales</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {group.stages.map(displayKey => {
                // bookedOpenPlay isn't a real backend stage — it's a
                // display-only split of 'registered' on openPlayReserved
                // (BK, 9/18/26). Cards keep stage='registered' for
                // script/outcome/drag logic; only the column they render in
                // and its header change.
                const isBookedOP = displayKey === 'bookedOpenPlay';
                const realStage = isBookedOP ? 'registered' : displayKey;
                const registeredAll = board.stages?.registered || [];
                let records = isBookedOP
                  ? registeredAll.filter(r => r.openPlayReserved)
                  : displayKey === 'registered'
                    ? registeredAll.filter(r => !r.openPlayReserved)
                    : (board.stages?.[displayKey] || []);
                // Newest session first on the club's next-step list: the fresher the session, the better the odds of a booking.
                if (displayKey === 'nextStep') records = [...records].sort((a, b) => String(b.sessionDateRaw).localeCompare(String(a.sessionDateRaw)));
                return (
                  <Column
                    key={displayKey}
                    stage={realStage}
                    metaKey={displayKey}
                    records={records}
                    scripts={scripts}
                    clubName={currentClub?.name}
                    onLogOutcome={handleLogOutcome}
                    onRefresh={load}
                    put={put}
                    onCardDrop={handleCardDrop}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {outcomeTarget && (
        <PB101OutcomeModal
          record={outcomeTarget}
          onClose={() => setOutcomeTarget(null)}
          onSaved={() => { setOutcomeTarget(null); load(); }}
        />
      )}
    </div>
  );
}
